import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker, useNavigation } from "react-day-picker";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3 pointer-events-auto", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        caption: "flex justify-center items-center pt-1 mb-4 relative",
        caption_label: "hidden",
        nav: "flex items-center gap-1",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-8 w-8 bg-background p-0 opacity-70 hover:opacity-100 hover:bg-accent",
        ),
        nav_button_previous: "",
        nav_button_next: "",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        day: cn(buttonVariants({ variant: "ghost" }), "h-9 w-9 p-0 font-normal aria-selected:opacity-100"),
        day_range_end: "day-range-end",
        day_selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        day_today: "bg-accent text-accent-foreground",
        day_outside:
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({ ..._props }) => <ChevronLeft className="h-4 w-4" />,
        IconRight: ({ ..._props }) => <ChevronRight className="h-4 w-4" />,
        Caption: ({ displayMonth }) => {
          const { goToMonth, nextMonth, previousMonth } = useNavigation();
          
          const currentYear = displayMonth.getFullYear();
          const currentMonth = displayMonth.getMonth();
          
          const years = Array.from({ length: 100 }, (_, i) => 1985 + i);
          const months = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
          ];

          const handleYearPrev = () => {
            const newDate = new Date(displayMonth);
            newDate.setFullYear(currentYear - 1);
            goToMonth(newDate);
          };

          const handleYearNext = () => {
            const newDate = new Date(displayMonth);
            newDate.setFullYear(currentYear + 1);
            goToMonth(newDate);
          };

          return (
            <div className="flex flex-col gap-2 mb-4">
              {/* Year Row */}
              <div className="flex justify-center items-center gap-2">
                <button
                  type="button"
                  onClick={handleYearPrev}
                  className={cn(
                    buttonVariants({ variant: "outline" }),
                    "h-8 w-8 bg-background p-0 opacity-70 hover:opacity-100 hover:bg-accent"
                  )}
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                <div className="flex items-center gap-2 min-w-[140px] justify-center">
                  <span className="text-sm font-medium text-muted-foreground">Year:</span>
                  <Select
                    value={currentYear.toString()}
                    onValueChange={(value) => {
                      const newDate = new Date(displayMonth);
                      newDate.setFullYear(parseInt(value));
                      goToMonth(newDate);
                    }}
                  >
                    <SelectTrigger className="w-[80px] h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-[200px]">
                      {years.map((year) => (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <button
                  type="button"
                  onClick={handleYearNext}
                  className={cn(
                    buttonVariants({ variant: "outline" }),
                    "h-8 w-8 bg-background p-0 opacity-70 hover:opacity-100 hover:bg-accent"
                  )}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>

              {/* Month Row */}
              <div className="flex justify-center items-center gap-2">
                <button
                  type="button"
                  onClick={() => previousMonth && goToMonth(previousMonth)}
                  disabled={!previousMonth}
                  className={cn(
                    buttonVariants({ variant: "outline" }),
                    "h-8 w-8 bg-background p-0 opacity-70 hover:opacity-100 hover:bg-accent disabled:opacity-30"
                  )}
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                <div className="flex items-center gap-2 min-w-[140px] justify-center">
                  <span className="text-sm font-medium text-muted-foreground">Month:</span>
                  <Select
                    value={currentMonth.toString()}
                    onValueChange={(value) => {
                      const newDate = new Date(displayMonth);
                      newDate.setMonth(parseInt(value));
                      goToMonth(newDate);
                    }}
                  >
                    <SelectTrigger className="w-[110px] h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map((month, index) => (
                        <SelectItem key={index} value={index.toString()}>
                          {month}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <button
                  type="button"
                  onClick={() => nextMonth && goToMonth(nextMonth)}
                  disabled={!nextMonth}
                  className={cn(
                    buttonVariants({ variant: "outline" }),
                    "h-8 w-8 bg-background p-0 opacity-70 hover:opacity-100 hover:bg-accent disabled:opacity-30"
                  )}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
        },
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
